import {
  addDays,
  subDays,
  addWeeks,
  eachMinuteOfInterval,
  format,
  isAfter,
  isBefore,
  isToday,
  isDate,
  isWithinInterval,
  setMilliseconds,
  setHours,
  subHours,
  setMinutes,
  startOfWeek,
  formatDistance,
} from "date-fns";
import find from "lodash/find";
import has from "lodash/has";

const MODIFY_CUTOFF_DAYS = 7;

/* v8 ignore start */
/**
 * What type of time style do we display for this rota?
 * shift-based (e.g. 'Afternoon') or plain time-based?
 *
 * @param {Object} rota
 * @returns String
 */
function rotaTimeStyle(rota) {
  if (rota.type === "care") {
    return "shift-based";
  }
  return "time-based";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the specific day of the rota, calculated from
 * the date plus a number of days since.
 * @param {Date} date
 * @param {int} dayIndex Integer representing the day number since the date
 * @returns {string} 01 Monday | 14 Saturday etc...
 */
function formatDateAsDay(date, dayIndex = 0) {
  try {
    return format(addDays(date, dayIndex), "dd EEEE");
  } catch (e) {
    console.error("[formatDateAsDay] Unable to format date", date, dayIndex, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the specific day of the rota, calculated from
 * the date plus a number of days since.
 * @param {Date} date
 * @param {int} dayIndex Integer representing the day number since the date
 * @returns {string} 01 Mon | 14 Sat etc...
 */
function formatDateAsShortDay(date, dayIndex = 0) {
  try {
    return format(addDays(date, dayIndex), "dd E");
  } catch (e) {
    console.error(
      "[formatDateAsShortDay] Unable to format date",
      date,
      dayIndex,
      e,
    );
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the specific day of the rota, calculated from
 * the date plus a number of days since.
 * @param {Date} date
 * @param {int} dayIndex Integer representing the day number since the date
 * @returns {string} 01 Monday | 14 Saturday etc...
 */
function formatDateAsDayWithSuffix(date, dayIndex = 0) {
  try {
    return format(addDays(date, dayIndex), "EEEE do MMMM");
  } catch (e) {
    console.error(
      "[formatDateAsDayWithSuffix] Unable to format date",
      date,
      dayIndex,
      e,
    );
  }
  return "";
}

function formatDateAsDayWithSuffixShortMonth(date, dayIndex = 0) {
  try {
    return format(addDays(date, dayIndex), "EEEE do MMM");
  } catch (e) {
    console.error(
      "[formatDateAsDayWithSuffix] Unable to format date",
      date,
      dayIndex,
      e,
    );
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format a date for an HTML date input, calculated from
 * the date plus a number of days since.
 * @param {Date} date
 * @param {int} dayIndex Integer representing the day number since the date
 * @returns {string}
 */
function formatDateForHtmlInput(date, dayIndex = 0) {
  try {
    return format(addDays(date, dayIndex), "yyyy-MM-dd");
  } catch (e) {
    console.error(
      "[formatDateForHtmlInput] Unable to format date",
      date,
      dayIndex,
      e,
    );
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
function formatDateForUkFormat(date) {
  try {
    return format(new Date(date), "dd-MM-yyyy");
  } catch (e) {
    console.error("[formatDateForUkFormat] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format a date to the hours & minutes portion.
 * @param {Date} date
 * @returns {string}
 */
function formatTimeHoursMins(date) {
  try {
    return format(date, "HH:mm");
  } catch (e) {
    console.error("[formatTimeHoursMins] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format a date to the hours & minutes portion in a 24h format, with day
 * @param {Date} date
 * @returns {string}
 */
function formatTimeHoursMinsWithDay(date) {
  try {
    return format(date, "EEE do, HH:mm");
  } catch (e) {
    console.error(
      "[formatTimeHoursMinsWithDay] Unable to format date",
      date,
      e,
    );
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format a date to the hours & minutes portion in 12h format.
 * @param {Date} date
 * @returns {string}
 */
function formatTimeHoursMins12h(date) {
  try {
    return format(date, "h:mm a");
  } catch (e) {
    console.error("[formatTimeHoursMins12h] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format a date to the hours & minutes portion in a 12h format, with day
 * @param {Date} date
 * @returns {string}
 */
function formatTimeHoursMins12hWithDay(date) {
  try {
    return format(date, "EEE do, h:mm a");
  } catch (e) {
    console.error(
      "[formatTimeHoursMins12hWithDay] Unable to format date",
      date,
      e,
    );
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the start date of a rota (short format)
 * @param {Date} date
 * @returns {string}
 */
function formatRotaStartDateShort(date) {
  try {
    return format(date, "do MMM");
  } catch (e) {
    console.error("[formatRotaStartDateShort] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the start date of a rota (short format with year)
 * @param {Date} date
 * @returns {string}
 */
function formatRotaStartDateShortWithYear(date) {
  try {
    return format(date, "do MMM yyyy");
  } catch (e) {
    console.error(
      "[formatRotaStartDateShortWithYear] Unable to format date",
      date,
      e,
    );
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format a start date (medium format)
 * @param {Date} date
 * @returns {string}
 */
function formatRotaStartDateMedium(date) {
  try {
    return format(date, "EEE, do MMM yyyy");
  } catch (e) {
    console.error("[formatRotaStartDateMedium] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the start date of a rota (long format)
 * @param {Date} date
 * @returns {string}
 */
function formatRotaStartDateLong(date) {
  try {
    return format(date, "EEEE, do MMMM yyyy");
  } catch (e) {
    console.error("[formatRotaStartDateLong] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format an entity modification date
 * @param {Date} date
 * @returns {string}
 */
function formatModifiedDate(date) {
  try {
    return format(date, "dd/MM/yyyy HH:mm");
  } catch (e) {
    console.error("[formatModifiedDate] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format an entity modification date
 * @param {Date} date
 * @returns {string}
 */
function formatModifiedDateLong(date) {
  try {
    return format(date, "EEEE do MMMM yyyy h:mm a");
  } catch (e) {
    console.error("[formatModifiedDateLong] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format month
 * @param {Date} date
 * @returns {string}
 */
function formatMonth(date) {
  try {
    return format(date, "MMMM yyyy");
  } catch (e) {
    console.error("[formatMonth] Unable to format date", date, e);
  }
  return "";
}
/* v8 ignore stop */

/* v8 ignore start */
/**
 * Format the hours & minutes portion from a session start/end prop
 * @param {object} session start or end object (e.g. session[0].staffStart)
 * @returns {string} "HH:mm"
 */
function formatTimeHoursMinsFromSession(sesssionProp) {
  return `${sesssionProp.hours
    .toString()
    .padStart(2, "0")}:${sesssionProp.minutes.toString().padStart(2, "0")}`;
}
/* v8 ignore stop */

/**
 * Is today the specific day of the rota, calculated from
 * the date plus a number of days since.
 * @param {Date} date
 * @param {int} dayIndex Integer representing the day number since the date
 * @returns {boolean}
 */
function rotaDateIsToday(date, dayIndex = 0) {
  return isToday(addDays(setMilliseconds(date, 0), dayIndex));
}

/**
 * Is today within the date range of the rota?
 * Optionally supply a range of days - this can be used to see if you're
 * in week 1 or not, used for automatic week selection.
 *
 * @param {Date} date
 * @param {int} range
 * @returns {boolean}
 */
function todayIsInRota(date, range = 14) {
  const date0Milliseconds = setMilliseconds(date, 0);
  return isWithinInterval(setMilliseconds(new Date(), 0), {
    start: date0Milliseconds,
    end: addDays(date0Milliseconds, range),
  });
}

/**
 * Take the date and time parts (hours/minutes only) from two seperate Date objects
 * and return a new Date object
 *
 * @param {Date} date
 * @param {Date} time
 * @returns {Date}
 */
function combineDateAndTime(date, time) {
  const dateTime = new Date(date);
  dateTime.setHours(time.getHours());
  dateTime.setMinutes(time.getMinutes());
  dateTime.setSeconds(0);
  dateTime.setMilliseconds(0);
  if (isBefore(dateTime, date)) {
    dateTime.setDate(dateTime.getDate() + 1);
  }
  return dateTime;
}

/**
 * Takes a given Date and returns an array that contains the startDate and endDate
 * of the previous 2 weeks respectively.
 *
 * @param {Date} date Starting date
 * @returns {Date[]} Array containing Date objects for the start and end date
 */
function getPreviousTwoWeekPeriod(date) {
  const dateTimeInStartWeek = addWeeks(date, -2);

  const startDate = startOfWeek(dateTimeInStartWeek, { weekStartsOn: 1 });
  const endDate = addDays(startDate, 13);

  return [startDate, endDate];
}

/**
 * Takes a given Date and returns an array that contains the startDate and endDate
 * of the previous 2 weeks respectively.
 *
 * @param {Date} date Starting date
 * @param {int} numberOfWeeks Number of weeks to go back
 * @returns {Date[]} Array containing Date objects for the start and end date
 */
function getPreviousNumberOfWeeksAsPeriod(
  date,
  numberOfWeeks = 8,
  includeCurrentDate = false,
) {
  const dateTimeInStartWeek = addWeeks(date, numberOfWeeks * -1);

  const startDate = startOfWeek(dateTimeInStartWeek, { weekStartsOn: 1 });
  let endDate = addWeeks(startDate, numberOfWeeks);
  if (includeCurrentDate) {
    // is the current date the end of the week?
    if (!isToday(endDate)) {
      endDate = addWeeks(endDate, 1);
    }
  }
  endDate = addDays(endDate, -1);

  return [startDate, endDate];
}

/**
 * CSS class helper to determine if a date is in the night or day.
 * Uses 6am and 6pm as day/night boundaries
 *
 * @param {String} time
 * @returns {Boolean}
 */
function isNighttime(time) {
  const dateObj = setHours(
    setMinutes(new Date(), time.split(":")[1], 0),
    time.split(":")[0],
  );
  const night =
    isBefore(dateObj, setHours(setMinutes(dateObj, 1), 6)) ||
    isAfter(dateObj, setHours(setMinutes(dateObj, 59), 17));
  return night;
}

/**
 * Format a time string and return a time for a HTML input, normalising it to remove seconds.
 *
 * @param {String} time
 * @returns {String}
 */
function formatTimeStringForHtmlInput(time) {
  const dateObj = setHours(
    setMinutes(new Date(), time.split(":")[1], 0),
    time.split(":")[0],
  );
  try {
    return format(dateObj, "HH:mm");
  } /* v8 ignore next 9 */ catch (e) {
    console.error(
      "[formatTimeStringForHtmlInput] Unable to format date",
      time,
      dateObj,
      e,
    );
  }
  return "";
}

/**
 * Determine if a string is a valid time string in the format HH:mm
 *
 * @param {string} timeString
 * @returns bool
 */
function isValidTimeString(timeString) {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(timeString);
}

/**
 * Given a list of activities and a date/time interval, return the coverage statistics for
 * activities in that interval. Used for calculating supervision hours and whether
 * they are fully covered by support activities.
 *
 * @param {Array} activities
 * @param {Object} interval
 * @returns {Object}
 */
function getActivityCoverageForInterval(activities, interval) {
  /* v8 ignore next 3 */
  if (!has(interval, "start") || !has(interval, "end")) {
    throw new Error("Interval must have start and end properties");
  }
  // brute force method - check each minute to see if we have an activity
  let timesToCheckForCoverage = eachMinuteOfInterval(interval);
  timesToCheckForCoverage.pop(); // remove the last one as it's the end of the interval

  const initialLength = timesToCheckForCoverage.length;
  // go through each time and remove it if we have an activity that matches
  timesToCheckForCoverage = timesToCheckForCoverage.filter(
    (time) =>
      find(
        activities,
        (activity) => activity.startDate <= time && activity.endDate > time,
      ) === undefined,
  );
  const result = {
    percentage:
      initialLength === 0
        ? 100
        : Math.floor(
            (1 - timesToCheckForCoverage.length / initialLength) * 100,
          ),
    minutesRemaining: timesToCheckForCoverage.length,
    minutesToCover: initialLength,
  };
  if (timesToCheckForCoverage.length > 0) {
    result.firstGap = timesToCheckForCoverage.slice(0, 1)[0];
  }
  return result;
}

function getFlexboxOrderStyleFromTime(dateTime) {
  if (!isDate(dateTime)) {
    if (dateTime.length === 8) {
      // hh:mm:ss
      return `order: ${dateTime.substring(0, 2)}${dateTime.substring(3, 5)};`;
    }
    throw new Error("Invalid time format");
  }

  const time = format(dateTime, "DDDHHmm", {
    useAdditionalDayOfYearTokens: true,
  });
  return `order: ${time};`;
}

function isBeyondModifyCutOff(date, now = null) {
  const cutOffDate = subDays(now || new Date(), MODIFY_CUTOFF_DAYS);
  return isBefore(date, cutOffDate);
}

/**
 * Returns a description of the time elapsed between the two provided dates,
 * formatted as "Day Name, Time Distance Ago".
 *
 * @param {Date|string} date - The starting date.
 * @param {Date|string} referenceDate - The date to compare against (defaults to the current date).
 * @returns {string} - The formatted description of the elapsed time.
 */
function describeTimeSince(date, referenceDate = new Date()) {
  try {
    const dayName = format(date, "EEEE");
    const timeDistance = formatDistance(date, referenceDate, {
      addSuffix: true,
    });
    return `${dayName}, ${timeDistance}`;
  } /* v8 ignore next 9 */ catch (e) {
    console.error(
      "[describeTimeSince] Unable to format date",
      date,
      referenceDate,
      e,
    );
  }
  return "";
}

/**
 * Formats the given number of minutes into hours and minutes.
 *
 * @param {number} minutes - The number of minutes to format.
 * @returns {string} The formatted string in the format "Xh Ym".
 */
function formatMinutesToHoursMinutes(minutes) {
  const clampedMinutes = Math.max(minutes, 0);
  const hours = Math.floor(clampedMinutes / 60);
  const mins = clampedMinutes % 60;
  return `${hours}h ${mins}m`;
}

/**
 * Defines whether a login or heartbeat event happened recently enough
 * to count as being here now.
 *
 * @param {Date} eventTimestamp - when the event occurred.
 * @param {Date} now - optional timestamp for 'now'
 * @returns {Boolean} whether event is recent
 */
function isRecentAuth(eventTimestamp, now = null) {
  return isAfter(eventTimestamp, subHours(now || new Date(), 12));
}

/**
 * Convert a string to an integer number checking it's within a specified
 * range.
 *
 * @param {String} string - the number as a string
 * @param {Object} range - the range min and max inclusive
 * @param {Number} range.min - the minumum number (inclusive, defaults to 0)
 * @param {Number} range.max - the maximum number allowed (inclusive)
 * @returns Number - the string as a number
 * @throws RangeError - if the string is not a number between min and max
 */
function parseIntRange(string, { min = 0, max }) {
  const integer = parseInt(string, 10);
  // note we use ! >= to catch NaNs which always evaluate to false
  if (!(integer >= min && integer <= max)) {
    throw new RangeError(`number must be between ${min} and ${max}`);
  }
  return integer;
}

/**
 * Resolve a string containing hours to a number.
 *
 * @param {String} hours - the hours as a string
 * @returns Number - the hours as a number
 * @throws RangeError - if the string is not a number between 0-23
 */
function parseHours(hours) {
  return parseIntRange(hours, { max: 23 });
}

/**
 * Resolve a string containing minutes to a number.
 *
 * @param {String} minutes - the minutes as a string
 * @returns Number - the minutes as a number
 * @throws RangeError - if the string is not a number between 0-59
 */
function parseMinutes(minutes) {
  return parseIntRange(minutes, { max: 59 });
}

/**
 * Resolve a time string into numeric hours and minutes.
 *
 * @param {String} time - the time as as hh:mm
 * @returns [Number, Number] - the hours and minutes as numbers
 * @throws RangeError - if the string is not hh:mm
 */
function parseTime(time) {
  const [hours, minutes] = time.split(":");
  return [parseHours(hours), parseMinutes(minutes)];
}

function parseTimeAsDateObject(time) {
  const [hours, minutes] = parseTime(time);
  return setMinutes(setHours(new Date(), hours), minutes);
}

/**
 * Resolve a day and a time into a date.
 *
 * @param {Date} day - a date from which the day will be used
 * @param {String} time - the time as as hh:mm
 * @returns {Date} - the date for the specified day and time
 */
function dateFromDayAndTime(day, time) {
  const date = new Date(day);
  const [hours, minutes] = parseTime(time);
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0, 0);
  return date;
}

/**
 * @typedef {Object} Interval
 * @property {Date} start - the resolved start date/time of the interval
 * @property {Date} end - the resolved end date/time of the interval
 */
/**
 * Resolve a duration just specified as start and end times into
 * concrete start and end dates for a specified day.
 *
 * @param {Date} day - a date from which the day will be used
 * @param {Object} times - an object specifying the start/end time as hh:mm
 * @param {String} times.startTime - the start time as as hh:mm
 * @param {String} times.endTime - the end time as as hh:mm
 * @returns {Interval} - the resolved interval dates
 */
function intervalFromDayAndTimes(day, { startTime, endTime }) {
  const start = dateFromDayAndTime(day, startTime);
  const end = dateFromDayAndTime(day, endTime);

  // CC-2093 make sure the activity time is inside the shift time span
  // if the start time is before the shift start time ($day), then it's probable
  // that the activity is for the next day (after midnight)
  // e.g. day = 2021-09-01 20:00:00, start = 2021-09-02 02:00:00
  if (start < day) {
    start.setDate(day.getDate() + 1);
    end.setDate(day.getDate() + 1);
  }

  if (!isAfter(end, start)) {
    // end time is either the same, or before the start time
    // so it must be 24 hour period, or the next day
    end.setDate(end.getDate() + 1);
  }

  return { start, end };
}

/**
 * Modifies date object to reset seconds/milliseconds to 0
 *
 * @param {Date} date
 */
/* v8 ignore start */
const resetSeconds = (date) => {
  if (date instanceof Date && date.getSeconds() !== 0) {
    date.setSeconds(0, 0);
  }
};
/* v8 ignore stop */

export {
  rotaTimeStyle,
  formatDateAsDay,
  formatDateAsShortDay,
  formatDateAsDayWithSuffix,
  formatDateAsDayWithSuffixShortMonth,
  formatDateForHtmlInput,
  formatDateForUkFormat,
  formatTimeHoursMins,
  formatTimeHoursMinsWithDay,
  formatTimeHoursMins12h,
  formatTimeHoursMins12hWithDay,
  formatRotaStartDateShort,
  formatRotaStartDateShortWithYear,
  formatRotaStartDateMedium,
  formatRotaStartDateLong,
  formatTimeHoursMinsFromSession,
  formatModifiedDate,
  formatModifiedDateLong,
  formatMonth,
  rotaDateIsToday,
  todayIsInRota,
  combineDateAndTime,
  getPreviousTwoWeekPeriod,
  getPreviousNumberOfWeeksAsPeriod,
  isNighttime,
  isValidTimeString,
  formatTimeStringForHtmlInput,
  getActivityCoverageForInterval,
  getFlexboxOrderStyleFromTime,
  isBeyondModifyCutOff,
  describeTimeSince,
  formatMinutesToHoursMinutes,
  isRecentAuth,
  intervalFromDayAndTimes,
  parseTimeAsDateObject,
  dateFromDayAndTime,
  resetSeconds,
};
